import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { Button, Modal, Spinner } from '../../ui';
import {
  BomVersionDetail,
  BomVersionMeta,
  SmartBomComponentEdit,
  TRIGGER_LABELS,
} from '../../../types/bomVersions';
import SmartBomGrid from './SmartBomGrid';
import ReasoningSheet from './ReasoningSheet';

/**
 * The Smart BOM viewer: an Excel-like modal with two sheets ("Smart BOM" |
 * "Reasoning", tab strip at the bottom like a workbook), a version picker
 * (v1 "Extracted" → vN), inline editing on the latest version for editors,
 * and a download that is always the LATEST version — the Smart BOM as it
 * stands now — whatever version is being browsed (the button says which).
 *
 * Fully presentational — state arrives via props (see useSmartBomVersions),
 * so the design gallery can render it with static sample data.
 */

export type SmartBomSheetTab = 'smart-bom' | 'reasoning';

export interface SmartBomModalProps {
  open: boolean;
  onClose: () => void;
  /** Sheet tab to land on each time the modal opens (default the grid; the
      reveal card opens straight onto "Reasoning" — the story of the changes). */
  initialTab?: SmartBomSheetTab;
  versions: BomVersionMeta[] | null;
  selectedVersion: number | null;
  detail: BomVersionDetail | null;
  loading: boolean;
  error: string | null;
  /** Viewer may edit (editor role) — editing still requires the latest version. */
  canEdit: boolean;
  saving: boolean;
  downloading: boolean;
  savedNotice: string | null;
  onSelectVersion: (version: number) => void;
  onSave: (edits: SmartBomComponentEdit[]) => void;
  onDownload: () => void;
  /** The sourcing criteria band under the workbook (a full `CriteriaBrief`). */
  criteria?: React.ReactNode;
  /** Start supplier research from the sheet (the pre-research checkpoint);
      omitted where research has already run. */
  onStartResearch?: () => void;
  /** "Ask Robbie to change" in the toolbar; omitted where the buyer cannot chat. */
  onAskRobbie?: () => void;
}

const versionLabel = (meta: BomVersionMeta): string => {
  const date = new Date(meta.createdAt);
  const dateText = Number.isNaN(date.getTime())
    ? ''
    : ` · ${date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
  const by = meta.actor === 'robbie' ? ' · Robbie' : '';
  return `v${meta.version} · ${TRIGGER_LABELS[meta.trigger] || meta.trigger}${by}${dateText}`;
};

export const SmartBomModal: React.FC<SmartBomModalProps> = ({
  open,
  onClose,
  initialTab = 'smart-bom',
  versions,
  selectedVersion,
  detail,
  loading,
  error,
  canEdit,
  saving,
  downloading,
  savedNotice,
  onSelectVersion,
  onSave,
  onDownload,
  criteria,
  onStartResearch,
  onAskRobbie,
}) => {
  const [tab, setTab] = useState<SmartBomSheetTab>(initialTab);
  const [edits, setEdits] = useState<SmartBomComponentEdit[]>([]);
  const [invalidCount, setInvalidCount] = useState(0);
  const [discardCounter, setDiscardCounter] = useState(0);

  // Each open lands on the caller's chosen sheet (grid by default).
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  const latestVersion = versions && versions.length > 0 ? versions[versions.length - 1].version : null;
  const viewingLatest = selectedVersion !== null && selectedVersion === latestVersion;
  const editable = canEdit && viewingLatest;
  const dirty = edits.length > 0;

  const resetKey = useMemo(
    () => `${detail?.bomId || ''}:${detail?.version ?? ''}:${discardCounter}`,
    [detail?.bomId, detail?.version, discardCounter],
  );

  const tabButton = (id: SmartBomSheetTab, label: string) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      aria-selected={tab === id}
      role="tab"
      className={`-mt-px rounded-b-control border border-t-0 px-s5 py-[5px] text-body-sm font-medium transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-action
        ${tab === id
          ? 'border-line bg-surface text-ink'
          : 'border-transparent text-ink-secondary hover:bg-surface-hover hover:text-ink'}`}
    >
      {label}
    </button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      flush
      title={
        <span className="flex items-baseline gap-s2">
          Smart BOM
          {selectedVersion !== null && (
            <span className="font-mono text-mono-data font-normal text-ink-secondary">v{selectedVersion}</span>
          )}
        </span>
      }
    >
      {/* Toolbar: version picker, position note, download */}
      <div className="flex flex-wrap items-center gap-s3 border-b border-line-divider px-s6 py-s3">
        <select
          aria-label="BOM version"
          value={selectedVersion ?? ''}
          onChange={(e) => onSelectVersion(Number(e.target.value))}
          disabled={!versions || versions.length === 0}
          className="h-7 rounded-control border border-line bg-surface px-s3 text-body-sm text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
        >
          {(versions || []).map((meta) => (
            <option key={meta.version} value={meta.version}>
              {versionLabel(meta)}
            </option>
          ))}
        </select>

        {versions && selectedVersion !== null && !viewingLatest && (
          <span className="text-meta text-ink-muted">
            viewing v{selectedVersion} of {versions.length} — read-only
          </span>
        )}
        {detail?.summary && viewingLatest && (
          <span className="max-w-[380px] truncate text-meta text-ink-muted max-md:max-w-none max-md:overflow-visible max-md:whitespace-normal" title={detail.summary}>
            {detail.summary}
          </span>
        )}

        <div className="ml-auto flex items-center gap-s3">
          {savedNotice && <span className="text-meta text-success-ink">{savedNotice}</span>}
          {error && (
            <span
              className="max-w-[300px] truncate text-meta text-danger-ink max-md:max-w-none max-md:overflow-visible max-md:whitespace-normal"
              title={error}
            >
              {error}
            </span>
          )}
          {onAskRobbie && (
            <Button variant="ghost" size="sm" onClick={onAskRobbie}>
              Ask Robbie to change
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={onDownload}
            disabled={latestVersion === null}
            loading={downloading}
            loadingLabel="Preparing…"
            iconLeft={<Download />}
            title="Downloads the latest version — the Smart BOM as it stands now"
          >
            {latestVersion !== null
                ? `Download .xlsx · v${latestVersion}`
                : 'Download .xlsx'}
          </Button>
        </div>
      </div>

      {/* Sheet body */}
      <div className="h-[440px] bg-surface">
        {loading || !detail ? (
          <div className="flex h-full items-center justify-center gap-s2 text-body-sm text-ink-muted">
            {loading ? (
              <>
                <Spinner /> Loading version…
              </>
            ) : (
              error || 'No version selected yet.'
            )}
          </div>
        ) : tab === 'smart-bom' ? (
          <SmartBomGrid
            components={detail.components}
            editable={editable}
            resetKey={resetKey}
            onDraftChange={(nextEdits, invalid) => {
              setEdits(nextEdits);
              setInvalidCount(invalid);
            }}
          />
        ) : (
          <ReasoningSheet rows={detail.reasoning} />
        )}
      </div>

      {/* Save bar — only when the sheet is actually dirty */}
      {editable && (dirty || invalidCount > 0) && tab === 'smart-bom' && (
        <div className="flex items-center justify-between border-t border-line-divider bg-canvas px-s6 py-s3">
          <span className="text-meta text-ink-secondary">
            {invalidCount > 0
              ? `${invalidCount} cell${invalidCount === 1 ? ' needs' : 's need'} a valid value before saving.`
              : `${edits.length} component${edits.length === 1 ? '' : 's'} edited — saving records a new version.`}
          </span>
          <div className="flex items-center gap-s2">
            <Button variant="ghost" size="sm" onClick={() => setDiscardCounter((c) => c + 1)} disabled={saving}>
              Discard
            </Button>
            <Button
              size="sm"
              onClick={() => onSave(edits)}
              disabled={invalidCount > 0 || edits.length === 0}
              loading={saving}
              loadingLabel="Saving…"
            >
              Save as new version
            </Button>
          </div>
        </div>
      )}

      {/* Excel-like sheet-tab strip, at the bottom like a workbook */}
      <div role="tablist" aria-label="Sheets" className="flex items-center gap-[2px] border-t border-line bg-canvas px-s5 pb-s2">
        {tabButton('smart-bom', 'Smart BOM')}
        {tabButton('reasoning', 'Reasoning')}
        <span className="ml-auto text-meta text-ink-muted">
          {detail ? `${detail.componentCount} component${detail.componentCount === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {/* What the research weighs this BOM against, with the step's next
          action bottom right — under the sheets, so the grid keeps its height. */}
      {(criteria || onStartResearch) && (
        <div className="flex items-center gap-s4 border-t border-robbie-border-soft bg-ai-panel px-s6 py-s3 max-md:flex-col max-md:items-stretch">
          <div className="min-w-0 flex-1">{criteria}</div>
          {onStartResearch && (
            <Button
              className="shrink-0"
              onClick={onStartResearch}
              disabled={dirty}
              title={dirty ? 'Save or discard your edits first' : 'Research runs against this BOM and these criteria'}
            >
              Start supplier research
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
};

export default SmartBomModal;
